import {
  AnimalType,
  MedType,
  IndicationType
} from '../types/dosage-types';
import {
  TreatmentGuideMap,
  TreatmentGuide,
  AdministrationStep,
  FAQ,
  TreatmentTip,
  NextStep
} from '../types/treatment-guide-types';

export const treatmentGuideData: TreatmentGuideMap = {
  [AnimalType.DOG]: {
    [MedType.TOLTRAZURIL]: {
      [IndicationType.TREATMENT]: {
        animalType: AnimalType.DOG,
        medType: MedType.TOLTRAZURIL,
        indicationType: IndicationType.TREATMENT,

        administrationSteps: [
          {
            step: 1,
            title: 'Prepare the Medication',
            description: 'Shake the toltrazuril solution well before use. Ensure the medication is at room temperature.',
            icon: '🧪',
            tips: ['Store medication in a cool, dry place', 'Check expiration date before use'],
            warnings: ['Do not use if solution has changed color or contains particles']
          },
          {
            step: 2,
            title: 'Calculate Precise Dose',
            description: 'Use the dosage calculator to determine the exact amount needed based on your dog\'s current weight.',
            icon: '⚖️',
            tips: ['Weigh your dog on the day of treatment for accuracy', 'Use a digital scale for precision']
          },
          {
            step: 3,
            title: 'Administer Orally',
            description: 'Give the medication directly into the mouth using a syringe or mix with a small amount of food.',
            icon: '💊',
            tips: ['Hold the dog\'s head slightly upward', 'Squirt slowly to prevent choking', 'Follow with a small treat'],
            warnings: ['Never force medication down the throat', 'Ensure the dog swallows completely']
          },
          {
            step: 4,
            title: 'Monitor for Side Effects',
            description: 'Watch your dog for any unusual reactions in the hours following administration.',
            icon: '👁️',
            tips: ['Normal appetite and behavior are good signs', 'Take photos of any concerning symptoms']
          }
        ],

        preparationNotes: [
          'Fast the dog for 2-3 hours before treatment if recommended by your veterinarian',
          'Have fresh water available at all times',
          'Prepare a small treat to give after medication',
          'Ensure you have the correct formulation and concentration'
        ],

        faqs: [
          {
            question: 'How quickly does toltrazuril work against coccidiosis?',
            answer: 'Toltrazuril typically begins working within 24-48 hours. You may notice improvement in stool consistency and reduced symptoms within 2-3 days of treatment.',
            category: 'effectiveness'
          },
          {
            question: 'Can I give toltrazuril with food?',
            answer: 'Yes, toltrazuril can be given with or without food. Mixing with a small amount of food may help with administration, especially for difficult dogs.',
            category: 'administration'
          },
          {
            question: 'What should I do if my dog vomits after receiving toltrazuril?',
            answer: 'If vomiting occurs within 30 minutes of administration, contact your veterinarian. They may recommend re-dosing or monitoring. Do not automatically give another dose.',
            category: 'safety'
          },
          {
            question: 'Is toltrazuril safe for puppies?',
            answer: 'Toltrazuril is generally safe for puppies over 2 weeks of age, but always consult your veterinarian for puppies under 8 weeks old.',
            category: 'safety'
          },
          {
            question: 'How long does it take to see results?',
            answer: 'Most dogs show improvement in symptoms within 2-4 days. Complete resolution of coccidiosis typically occurs within 5-7 days of treatment.',
            category: 'effectiveness'
          },
          {
            question: 'Can I use toltrazuril for prevention?',
            answer: 'Toltrazuril is primarily a treatment medication. For prevention, discuss appropriate protocols with your veterinarian based on your dog\'s risk factors.',
            category: 'general'
          }
        ],

        tips: [
          {
            title: 'Accurate Dosing is Critical',
            description: 'Always use a calibrated syringe or measuring device. Kitchen spoons are not accurate enough for medication dosing.',
            icon: '🎯',
            priority: 'high'
          },
          {
            title: 'Keep Environment Clean',
            description: 'Clean and disinfect areas where your dog eliminates to prevent reinfection and spread to other pets.',
            icon: '🧽',
            priority: 'high'
          },
          {
            title: 'Monitor Hydration',
            description: 'Ensure your dog has access to fresh water at all times, especially if they experienced diarrhea.',
            icon: '💧',
            priority: 'medium'
          },
          {
            title: 'Separate Infected Animals',
            description: 'Keep treated dogs away from other pets until symptoms resolve to prevent spread of infection.',
            icon: '🏠',
            priority: 'medium'
          },
          {
            title: 'Complete the Full Treatment',
            description: 'Even if symptoms improve quickly, complete the full treatment regimen as prescribed.',
            icon: '✅',
            priority: 'high'
          }
        ],

        nextSteps: [
          {
            title: 'Monitor Recovery',
            description: 'Watch for improvement in stool consistency and overall energy levels.',
            timeframe: '2-4 days',
            action: 'Observe and document any changes',
            icon: '📊'
          },
          {
            title: 'Follow-up Examination',
            description: 'Schedule a check-up to confirm the infection has been eliminated.',
            timeframe: '7-10 days',
            action: 'Contact your veterinarian',
            icon: '🩺'
          },
          {
            title: 'Fecal Testing',
            description: 'A follow-up fecal examination may be recommended to confirm treatment success.',
            timeframe: '10-14 days',
            action: 'Collect fresh stool sample',
            icon: '🔬'
          },
          {
            title: 'Hygiene Review',
            description: 'Implement prevention strategies to avoid future infections.',
            timeframe: 'Ongoing',
            action: 'Maintain clean environment',
            icon: '🛡️'
          }
        ],

        relatedArticles: [
          'Understanding Coccidiosis in Dogs',
          'Preventing Parasitic Infections',
          'When to Contact Your Veterinarian'
        ]
      }
    },

    [MedType.FENBENDAZOLE]: {
      [IndicationType.TREATMENT]: {
        animalType: AnimalType.DOG,
        medType: MedType.FENBENDAZOLE,
        indicationType: IndicationType.TREATMENT,

        administrationSteps: [
          {
            step: 1,
            title: 'Measure Accurate Dose',
            description: 'Calculate the correct dose based on your dog\'s current weight using the dosage calculator.',
            icon: '📏',
            tips: ['Weigh your dog before each treatment cycle', 'Use the same scale for consistency']
          },
          {
            step: 2,
            title: 'Prepare Administration',
            description: 'Fenbendazole can be given with food to improve palatability and reduce stomach upset.',
            icon: '🍽️',
            tips: ['Mix with wet food or treats', 'Ensure the entire dose is consumed']
          },
          {
            step: 3,
            title: 'Give Daily for 3 Days',
            description: 'Administer the dose once daily for three consecutive days. Maintain consistent timing.',
            icon: '📅',
            tips: ['Set a daily reminder', 'Give at the same time each day'],
            warnings: ['Do not skip doses - complete the full 3-day course']
          },
          {
            step: 4,
            title: 'Monitor and Clean',
            description: 'Watch for expelled worms and maintain environmental hygiene.',
            icon: '🔍',
            tips: ['Clean up feces promptly', 'Wash hands thoroughly after handling']
          }
        ],

        preparationNotes: [
          'Fenbendazole can be given with or without food',
          'Ensure all pets in the household are treated simultaneously',
          'Have cleaning supplies ready for post-treatment cleanup',
          'Store medication in a cool, dry place'
        ],

        faqs: [
          {
            question: 'How many days should I give fenbendazole?',
            answer: 'The standard treatment is once daily for 3 consecutive days. Some severe infections may require extended treatment as directed by your veterinarian.',
            category: 'administration'
          },
          {
            question: 'Will I see worms in my dog\'s stool?',
            answer: 'You may see dead or dying worms in the stool for several days after treatment. This is normal and indicates the medication is working.',
            category: 'effectiveness'
          },
          {
            question: 'Is fenbendazole safe for pregnant dogs?',
            answer: 'Yes, fenbendazole is considered safe for pregnant and nursing dogs. It\'s often recommended for pregnant females to prevent transmission to puppies.',
            category: 'safety'
          },
          {
            question: 'Can I treat all my pets at the same time?',
            answer: 'Yes, it\'s recommended to treat all dogs and cats in the household simultaneously to prevent reinfection.',
            category: 'general'
          }
        ],

        tips: [
          {
            title: 'Treat All Pets Together',
            description: 'Deworm all pets in the household at the same time to prevent cross-contamination.',
            icon: '🐕‍🦺',
            priority: 'high'
          },
          {
            title: 'Environmental Cleanup',
            description: 'Clean areas where pets eliminate and wash bedding in hot water.',
            icon: '🧼',
            priority: 'high'
          },
          {
            title: 'Regular Prevention',
            description: 'Discuss a regular deworming schedule with your veterinarian.',
            icon: '🗓️',
            priority: 'medium'
          }
        ],

        nextSteps: [
          {
            title: 'Complete 3-Day Course',
            description: 'Finish all three daily doses even if symptoms improve.',
            timeframe: '3 days',
            action: 'Continue medication as prescribed',
            icon: '💊'
          },
          {
            title: 'Monitor for Worms',
            description: 'Watch for expelled worms in stool over the next week.',
            timeframe: '7 days',
            action: 'Observe and clean up promptly',
            icon: '👀'
          },
          {
            title: 'Follow-up Testing',
            description: 'Fecal examination to confirm treatment success.',
            timeframe: '2-4 weeks',
            action: 'Schedule vet appointment',
            icon: '🔬'
          }
        ]
      }
    }
  },

  [AnimalType.CHICKEN]: {
    [MedType.TOLTRAZURIL]: {
      [IndicationType.TREATMENT]: {
        animalType: AnimalType.CHICKEN,
        medType: MedType.TOLTRAZURIL,
        indicationType: IndicationType.TREATMENT,

        administrationSteps: [
          {
            step: 1,
            title: 'Calculate Water Treatment',
            description: 'Determine the amount of toltrazuril needed for your flock\'s daily water consumption.',
            icon: '💧',
            tips: ['Chickens typically drink 200-400ml per day', 'Consider temperature - birds drink more in heat'],
            warnings: ['Remove all other water sources during treatment']
          },
          {
            step: 2,
            title: 'Mix Fresh Daily',
            description: 'Prepare fresh medicated water each day for 2 consecutive days.',
            icon: '🥤',
            tips: ['Use clean water containers', 'Mix thoroughly before offering'],
            warnings: ['Discard any leftover medicated water after 24 hours']
          },
          {
            step: 3,
            title: 'Ensure Consumption',
            description: 'Monitor that all birds are drinking the medicated water.',
            icon: '👁️',
            tips: ['Place multiple water stations for large flocks', 'Check water levels regularly'],
            warnings: ['Weak birds may need individual treatment']
          },
          {
            step: 4,
            title: 'Maintain Hygiene',
            description: 'Clean and disinfect coops, runs, and equipment.',
            icon: '🧽',
            tips: ['Remove wet bedding daily', 'Disinfect water containers between uses']
          }
        ],

        preparationNotes: [
          'Calculate total flock water consumption accurately',
          'Ensure no other water sources are available',
          'Have backup water ready if birds refuse medicated water',
          'Isolate severely affected birds for individual treatment'
        ],

        faqs: [
          {
            question: 'How do I know if my chickens are drinking enough medicated water?',
            answer: 'Monitor water consumption levels and watch for clinical improvement. Healthy chickens will maintain normal drinking patterns.',
            category: 'administration'
          },
          {
            question: 'Can I eat eggs during treatment?',
            answer: 'There is no withdrawal period for eggs when using toltrazuril in chickens, but consult your veterinarian for specific guidance.',
            category: 'safety'
          },
          {
            question: 'What if some birds won\'t drink the medicated water?',
            answer: 'Severely affected birds may need individual oral dosing. Consult your veterinarian for alternative administration methods.',
            category: 'administration'
          },
          {
            question: 'How quickly should I see improvement?',
            answer: 'Most chickens show improvement in droppings and activity within 24-48 hours of starting treatment.',
            category: 'effectiveness'
          }
        ],

        tips: [
          {
            title: 'Flock-Wide Treatment',
            description: 'Treat the entire flock simultaneously, even apparently healthy birds.',
            icon: '🐓',
            priority: 'high'
          },
          {
            title: 'Biosecurity Measures',
            description: 'Implement strict cleaning protocols to prevent reinfection.',
            icon: '🛡️',
            priority: 'high'
          },
          {
            title: 'Nutritional Support',
            description: 'Provide high-quality feed and electrolytes to support recovery.',
            icon: '🌾',
            priority: 'medium'
          }
        ],

        nextSteps: [
          {
            title: 'Complete 2-Day Treatment',
            description: 'Ensure all birds receive medicated water for both days.',
            timeframe: '2 days',
            action: 'Monitor water consumption',
            icon: '💧'
          },
          {
            title: 'Observe Recovery',
            description: 'Watch for improvement in droppings and bird activity.',
            timeframe: '3-5 days',
            action: 'Document any changes',
            icon: '📝'
          },
          {
            title: 'Environmental Cleaning',
            description: 'Deep clean and disinfect housing areas.',
            timeframe: '1 week',
            action: 'Complete coop sanitization',
            icon: '🏠'
          }
        ]
      }
    }
  }
};

// Helper function to get treatment guide
export function getTreatmentGuide(
  animalType: AnimalType,
  medType: MedType,
  indicationType: IndicationType
): TreatmentGuide | undefined {
  return treatmentGuideData[animalType]?.[medType]?.[indicationType];
}